import { ApiClientError, isRecord, requestJson } from "./http.js";

/**
 * Admin override for an account that lost every trusted device: one admin requests it, a second
 * one approves, and the account's next sign-in from a new device is trusted once.
 */
export interface TrustOverride {
  readonly id: string;
  readonly userId: string;
  readonly email: string | null;
  readonly requestedBy: string;
  readonly requestedByEmail: string | null;
  readonly approvedBy: string | null;
  readonly approvedByEmail: string | null;
  /** "pending" waits for the second admin, "approved" for the member's sign-in. */
  readonly status: string;
  readonly createdAt: number;
  readonly approvedAt: number | null;
  readonly expiresAt: number;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

export function isTrustOverride(value: unknown): value is TrustOverride {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["userId"] === "string" &&
    isNullableString(value["email"]) &&
    typeof value["requestedBy"] === "string" &&
    isNullableString(value["requestedByEmail"]) &&
    isNullableString(value["approvedBy"]) &&
    isNullableString(value["approvedByEmail"]) &&
    typeof value["status"] === "string" &&
    typeof value["createdAt"] === "number" &&
    (value["approvedAt"] === null || typeof value["approvedAt"] === "number") &&
    typeof value["expiresAt"] === "number";
}

function unexpected(): ApiClientError {
  return new ApiClientError(500, "Unerwartete Antwort vom Server.");
}

function overrideFrom(body: unknown): TrustOverride {
  if (!isRecord(body) || !isTrustOverride(body["override"])) {
    throw unexpected();
  }
  return body["override"];
}

export async function fetchTrustOverrides(): Promise<ReadonlyArray<TrustOverride>> {
  const body = await requestJson("/api/v1/admin/trust-overrides");
  if (!isRecord(body) || !Array.isArray(body["overrides"]) || !body["overrides"].every(isTrustOverride)) {
    throw unexpected();
  }
  return body["overrides"];
}

/** Files the request; nothing changes until a second admin approves. */
export async function requestTrustOverride(userId: string): Promise<TrustOverride> {
  return overrideFrom(await requestJson(`/api/v1/admin/users/${userId}/trust-override`, { method: "POST", body: {} }));
}

export async function approveTrustOverride(id: string): Promise<TrustOverride> {
  return overrideFrom(await requestJson(`/api/v1/admin/trust-overrides/${id}/approve`, { method: "POST", body: {} }));
}

export async function cancelTrustOverride(id: string): Promise<void> {
  await requestJson(`/api/v1/admin/trust-overrides/${id}/cancel`, { method: "POST", body: {} });
}
